import type { GlobalState } from '../global/types';

import { IS_MOCKED_CLIENT } from '../config';
import { loadCache, loadCachedSharedState } from '../global/cache';
import {
  getGlobal, setGlobal,
} from '../global/index';
import { INITIAL_GLOBAL_STATE } from '../global/initialState';
import { updatePasscodeSettings } from '../global/reducers';
import { cloneDeep } from './iteratees';
import { DEFAULT_FORENSIC_SETTINGS } from '../global/types/sharedState';
import { DEFAULT_RASITECH_MODULES } from '../global/types/sharedState';
import { DEFAULT_RASITECH_SETTINGS, getModFeatureSettings, setModFeatureCache } from './modFeatures';
import { getRasiTechModules, setRasiTechModulesCache } from './modModules';
import { clearStoredSession } from './sessions';

export async function initGlobal(force: boolean = false, prevGlobal?: GlobalState) {
  prevGlobal = prevGlobal || getGlobal();
  if (!force && 'byTabId' in prevGlobal) {
    return;
  }

  const initial = cloneDeep(INITIAL_GLOBAL_STATE);
  const cache = await loadCache(initial);
  let global = cache || initial;
  if (IS_MOCKED_CLIENT) global.auth.state = 'authorizationStateReady';

  const { hasPasscode, isScreenLocked } = global.passcode;
  if (hasPasscode && !isScreenLocked) {
    global = updatePasscodeSettings(global, {
      isScreenLocked: true,
    });

    clearStoredSession();
  }

  if (force) {
    global.byTabId = prevGlobal.byTabId;
  }

  if (!cache) { // Try loading shared state separately
    const storedSharedState = await loadCachedSharedState();
    if (storedSharedState) {
      global.sharedState = storedSharedState;
    }
  }

  const rasiTechModules = {
    ...DEFAULT_RASITECH_MODULES,
    ...global.sharedState.settings.rasiTechModules,
    ...getRasiTechModules(),
  };
  const rasiTech = {
    ...DEFAULT_RASITECH_SETTINGS,
    ...global.sharedState.settings.rasiTech,
    ...getModFeatureSettings(),
    antiDelete: rasiTechModules.privateMode.restoreDeletedMessages,
    antiEditHistory: rasiTechModules.privateMode.showEditedWithOriginal,
    unrestrictMedia: true,
  };
  const forensic = {
    ...DEFAULT_FORENSIC_SETTINGS,
    ...global.sharedState.settings.forensic,
  };
  setModFeatureCache(rasiTech);
  setRasiTechModulesCache(rasiTechModules);
  global = {
    ...global,
    sharedState: {
      ...global.sharedState,
      settings: {
        ...global.sharedState.settings,
        rasiTech,
        rasiTechModules,
        forensic,
      },
    },
  };

  setGlobal(global);
}
